import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Chip,
  Paper,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications,
  Search,
  MarkEmailRead,
  Clear,
  Circle,
  NotificationsOff,
  Refresh,
  Warning,
  Message,
  BookmarkBorder,
  Analytics
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { getPrimaryBlendedColor } from '../../utils/themeColor';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import gasiqlogo from '../../img/zaplogo_square.png';

// ENHANCED Import Firebase functions - matching DesktopHeader
import {
  addNotificationUpdateListener,
  initializeAppWithFCM,
  testNotification,
  clearAppBadge,
  setAppBadge,
  syncNotificationsFromServiceWorker,
  markNotificationsAsReadInServiceWorker,
  clearNotificationsInServiceWorker
} from '../../firebase';
import { useAuth } from '../../pages/authentication/AuthContext';

// Notification item component for mobile (more compact)
const MobileNotificationItem = ({ notification, onMarkAsRead, onRemove }) => {
  const theme = useTheme();

  const getNotificationIcon = (type) => {
    const iconProps = {
      sx: {
        width: 14,
        height: 14,
        color: theme.palette.text.secondary
      }
    };

    switch (type) {
      case 'message':
        return <Message {...iconProps} />;
      case 'course':
        return <BookmarkBorder {...iconProps} />;
      case 'progress':
        return <Analytics {...iconProps} />;
      default:
        return <Circle {...iconProps} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'message':
        return theme.palette.info.main;
      case 'course':
        return theme.palette.success.main;
      case 'progress':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <ListItem
      sx={{
        bgcolor: notification.read ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
        borderLeft: `2px solid ${notification.read ? 'transparent' : getTypeColor(notification.type)}`,
        '&:hover': {
          bgcolor: alpha(theme.palette.action.hover, 0.04)
        },
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        py: 1
      }}
      onClick={() => {
        if (!notification.read) {
          onMarkAsRead(notification.id);
        }
        if (notification.url) {
          window.location.href = notification.url;
        }
      }}
    >
      <ListItemAvatar sx={{ minWidth: 32 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: alpha(getTypeColor(notification.type), 0.1),
            color: getTypeColor(notification.type)
          }}
        >
          {getNotificationIcon(notification.type)}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.read ? 400 : 600,
                color: theme.palette.text.primary,
                flex: 1,
                fontSize: '0.875rem'
              }}
            >
              {notification.title}
            </Typography>
            {!notification.read && (
              <Circle
                sx={{
                  width: 6,
                  height: 6,
                  color: theme.palette.primary.main
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
                mb: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {notification.body}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={notification.type}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.7rem',
                  bgcolor: alpha(getTypeColor(notification.type), 0.1),
                  color: getTypeColor(notification.type),
                  '& .MuiChip-label': { px: 1 }
                }}
              />
              <Typography
                variant="caption"
                sx={{ 
                  color: theme.palette.text.disabled,
                  fontSize: '0.7rem'
                }}
              >
                {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>
        }
      />

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(notification.id);
        }}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s',
          p: 0.5,
          '&:hover': { opacity: 1 },
          '.MuiListItem-root:hover &': { opacity: 1 }
        }}
      >
        <Clear sx={{ fontSize: 14 }} />
      </IconButton>
    </ListItem>
  );
};

// Enhanced Mobile App Bar Component
const MobileAppBar = ({ toggleMobileDrawer, handleProfileMenuOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  // ENHANCED: State management matching DesktopHeader
  const [notifications, setNotifications] = useState([]);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const removeNotificationListener = useRef(null);

  // ENHANCED: Notification update handler that syncs with service worker
  const handleNotificationUpdate = useCallback((allNotifications, unreadCount) => {
    console.log('[MobileAppBar] Received notification update:', {
      total: allNotifications.length,
      unread: unreadCount
    });
    
    setNotifications(allNotifications);
    setAppBadge(unreadCount);
    setIsLoading(false);
  }, []);

  // ENHANCED: Initialize Firebase and load existing notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        console.log('[MobileAppBar] Initializing Firebase messaging...');
        
        // Initialize Firebase messaging
        const result = await initializeAppWithFCM();
        if (result.success) {
          setIsFirebaseInitialized(true);
          console.log('[MobileAppBar] Firebase messaging initialized successfully');
        } else {
          console.warn('[MobileAppBar] Failed to initialize Firebase messaging:', result.error);
        }

        // IMPORTANT: Load existing notifications immediately
        console.log('[MobileAppBar] Loading existing notifications...');
        const existingNotifications = await syncNotificationsFromServiceWorker();
        console.log('[MobileAppBar] Loaded existing notifications:', existingNotifications.length);
        
        if (existingNotifications.length > 0) {
          setNotifications(existingNotifications);
          const unreadCount = existingNotifications.filter(n => !n.read).length;
          setAppBadge(unreadCount);
        }
        
        setIsLoading(false);
        
      } catch (error) {
        console.error('[MobileAppBar] Error during Firebase initialization:', error);
        setIsLoading(false);
      }
    };

    // Setup the enhanced notification listener for real-time updates
    console.log('[MobileAppBar] Setting up notification update listener...');
    removeNotificationListener.current = addNotificationUpdateListener(handleNotificationUpdate);

    // Initialize Firebase and load notifications
    initializeNotifications();

    // Cleanup function
    return () => {
      if (removeNotificationListener.current) {
        console.log('[MobileAppBar] Cleaning up notification listener');
        removeNotificationListener.current();
      }
    };
  }, [handleNotificationUpdate]);

  // ENHANCED: Re-sync notifications when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('[MobileAppBar] App became visible, syncing notifications...');
        try {
          const syncedNotifications = await syncNotificationsFromServiceWorker();
          setNotifications(syncedNotifications);
          const unreadCount = syncedNotifications.filter(n => !n.read).length;
          setAppBadge(unreadCount);
        } catch (error) {
          console.error('[MobileAppBar] Error syncing on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Calculate unread notifications count
  const unreadCount = notifications.filter(n => !n.read).length;

  // ENHANCED: Handle notification icon click with sync
  const handleNotificationClick = async (event) => {
    setNotificationAnchorEl(event.currentTarget);

    // Sync notifications when opening popover
    try {
      console.log('[MobileAppBar] Syncing notifications on popover open...');
      const updatedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(updatedNotifications || []);
      const currentUnreadCount = updatedNotifications.filter(n => !n.read).length;
      setAppBadge(currentUnreadCount);
    } catch (error) {
      console.error('[MobileAppBar] Error syncing notifications on open:', error);
    }
  };

  // Handle notification popover close
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  // ENHANCED: Mark notification as read with service worker sync
  const markAsRead = async (notificationId) => {
    try {
      console.log('[MobileAppBar] Marking notification as read:', notificationId);
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      // Sync with service worker
      await markNotificationsAsReadInServiceWorker([notificationId]);
      
    } catch (error) {
      console.error('[MobileAppBar] Error marking notification as read:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Remove notification with service worker sync
  const removeNotification = async (notificationId) => {
    try {
      console.log('[MobileAppBar] Removing notification:', notificationId);
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.filter(notification => notification.id !== notificationId)
      );

      // Sync with service worker
      await clearNotificationsInServiceWorker([notificationId]);
      
    } catch (error) {
      console.error('[MobileAppBar] Error removing notification:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Mark all as read with service worker sync
  const markAllAsRead = async () => {
    try {
      console.log('[MobileAppBar] Marking all notifications as read');
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      clearAppBadge();

      // Sync with service worker (empty array means mark all as read)
      await markNotificationsAsReadInServiceWorker([]);
      
    } catch (error) {
      console.error('[MobileAppBar] Error marking all as read:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Clear all notifications with service worker sync
  const clearAllNotifications = async () => {
    try {
      console.log('[MobileAppBar] Clearing all notifications');
      
      // Optimistically update UI first
      setNotifications([]);
      clearAppBadge();
      handleNotificationClose();

      // Sync with service worker (empty array means clear all)
      await clearNotificationsInServiceWorker([]);
      
    } catch (error) {
      console.error('[MobileAppBar] Error clearing all notifications:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Test notification function
  const handleTestNotification = async () => {
    try {
      console.log('[MobileAppBar] Sending test notification...');
      const testNotif = await testNotification();
      console.log('[MobileAppBar] Test notification sent:', testNotif);
    } catch (error) {
      console.error('[MobileAppBar] Error sending test notification:', error);
    }
  };

  const isNotificationOpen = Boolean(notificationAnchorEl);
  const appBarColor = getPrimaryBlendedColor(theme.palette.primary.main);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: appBarColor,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 1px 8px ${alpha(theme.palette.common.black, 0.06)}`,
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: theme.spacing(2) }}>
          {/* Brand Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <img
              src={gasiqlogo}
              alt="Brand logo"
              style={{ borderRadius: "50%", width: "46px", height: "46px", objectFit: "contain", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
            />
          </Box>

          {/* Right side - Search, Notifications & Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            {/* Search Icon */}
            <Tooltip title="Search">
              <IconButton 
                sx={{ color: theme.palette.text.primary, p: 1 }}
              >
                <Search sx={{ fontSize: 20,color: theme.palette.action }} />
              </IconButton>
            </Tooltip>

            {/* Test Notification Button (Development only) */}
            {/* {process.env.NODE_ENV === 'development' && (
              <Tooltip title="Test Notification">
                <IconButton
                  onClick={handleTestNotification}
                  disabled={!isFirebaseInitialized}
                  sx={{
                    color: theme.palette.text.secondary,
                    p: 0.5,
                    fontSize: '0.7rem'
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                    T
                  </Typography>
                </IconButton>
              </Tooltip>
            )} */}

            {/* Notifications Icon */}
            <Tooltip title={`${unreadCount} unread notifications`}>
              <IconButton 
                onClick={handleNotificationClick}
                sx={{ color: theme.palette.text.primary, p: 1 }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  {isLoading ? (
                    <Notifications sx={{ fontSize: 20,color: theme.palette.action, opacity: 0.5 }} />
                  ) : notifications.length === 0 ? (
                    <NotificationsOff sx={{ fontSize: 20,color: theme.palette.action }} />
                  ) : (
                    <Notifications sx={{ fontSize: 20,color: theme.palette.action }} />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Profile Avatar */}
            <Avatar
              src={user?.profile_pic ? `${process.env.REACT_APP_API_URL}${user.profile_pic}` : undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: theme.palette.primary.main,
                cursor: 'pointer',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
              onClick={handleProfileMenuOpen}
            >
              {`${user?.first_name?.charAt(0).toUpperCase() ?? ''}${user?.last_name?.charAt(0).toUpperCase() ?? ''}`}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Enhanced Mobile Notification Popover */}
      <Popover
        open={isNotificationOpen}
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '90vw' : 380,
            maxWidth: 400,
            maxHeight: isMobile ? '70vh' : 500,
            mt: 1,
            mx: isMobile ? 1 : 0,
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <Paper elevation={0}>
          {/* Header */}
          <Box
            sx={{
              p: isMobile ? 1.5 : 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              sx={{ fontWeight: 600 }}
            >
              Notifications {isLoading && '(Loading...)'}
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton
                    size="small"
                    onClick={markAllAsRead}
                    disabled={isLoading}
                    sx={{ p: 0.5 }}
                  >
                    <MarkEmailRead sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {notifications.length > 0 && (
                <Tooltip title="Clear all">
                  <IconButton
                    size="small"
                    onClick={clearAllNotifications}
                    color="error"
                    disabled={isLoading}
                    sx={{ p: 0.5 }}
                  >
                    <Clear sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Notifications List */}
          {isLoading ? (
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                textAlign: 'center',
                color: theme.palette.text.secondary
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Loading notifications...
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              sx={{
                p: isMobile ? 3 : 4,
                textAlign: 'center',
                color: theme.palette.text.secondary
              }}
            >
              <NotificationsOff sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                No notifications yet
              </Typography>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  size="small"
                  onClick={handleTestNotification}
                  sx={{ mt: 1 }}
                  disabled={!isFirebaseInitialized}
                >
                  Send Test Notification
                </Button>
              )}
            </Box>
          ) : (
            <List 
              sx={{ 
                p: 0, 
                maxHeight: isMobile ? '50vh' : 350, 
                overflow: 'auto' 
              }}
            >
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <MobileNotificationItem
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onRemove={removeNotification}
                  />
                  {index < notifications.length - 1 && (
                    <Divider sx={{ ml: isMobile ? 5 : 6 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Footer */}
          {notifications.length > 0 && !isLoading && (
            <Box
              sx={{
                p: isMobile ? 1 : 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center'
              }}
            >
              <Button
                size="small"
                fullWidth
                onClick={() => {
                  handleNotificationClose();
                  // Navigate to notifications page
                  navigate(`/${user?.role}/notifications`);
                }}
                sx={{ fontSize: '0.875rem' }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </Paper>
      </Popover>
    </>
  );
};

export default MobileAppBar;